import React from 'react';
import { BoxProps } from '@mantine/core';
import { Table as TanstackTable, Row as TanstackRow } from '@tanstack/react-table';
import { UseTableGridLayoutReturn } from './hooks/useTableGridLayout';

// Base type constraint for all table data
export type TableData = Record<string, unknown>;

export interface MantineTableMeta {
	selectAll: boolean;
	setSelectAll: (value: boolean) => void;
	greedySelecting: boolean;
	setGreedySelecting: (value: boolean) => void;
	selectionScope: 'All' | 'Page';
	setSelectionScope: (scope: 'All' | 'Page' | null) => void;

	// UI state
	showGlobalFilter?: boolean;
	setShowGlobalFilter?: (value: boolean) => void;
	showColumnFilters?: boolean;
	setShowColumnFilters?: (value: boolean) => void;
	isFullScreen?: boolean;
	setIsFullScreen?: (value: boolean) => void;

	// Grid layout
	gridLayout?: UseTableGridLayoutReturn;

	pinnedColumns?: Record<string, number>;
	lastPinnedColumnId?: string;

	// Pagination
	enablePagination: boolean;
	manualPagination: boolean;

	// Testing
	dataTestId?: string;

	// Action state reset
	resetActionState?: () => void;
}

export interface MantineTable<T extends TableData> extends TanstackTable<T> {
	options: TanstackTable<T>['options'] & {
		meta?: MantineTableMeta;
		rowCount?: number;
	};
}

export type RowActionRenderer<T extends TableData> = (args: {
	row: TanstackRow<T>;
	table: TanstackTable<T>;
}) => React.ReactNode;

export type CellPropsRenderer<T extends TableData> = (args: {
	row: TanstackRow<T>;
	cell: ReturnType<TanstackRow<T>['getVisibleCells']>[number];
	table: TanstackTable<T>;
}) => React.HTMLProps<HTMLDivElement> & BoxProps;

export type RowPropsRenderer<T extends TableData> = (props: {
	isDetailPanel?: boolean;
	row: TanstackRow<T>;
	staticRowIndex: number;
	table: TanstackTable<T>;
}) => (React.HTMLProps<HTMLDivElement> & {
	ref?: React.MutableRefObject<HTMLDivElement | null> | null | undefined;
}) &
	BoxProps;

export type DetailPanelRenderer<T extends TableData> = (args: {
	row: TanstackRow<T>;
	table: TanstackTable<T>;
}) => React.ReactNode;

export type ContainerPropsRenderer<T extends TableData> = (args: {
	table: TanstackTable<T>;
}) => React.HTMLProps<HTMLDivElement> & BoxProps;

export type DragDropHandler<T extends TableData> = (dropInfo: {
	movedRowId: string;
	oldIndex: number;
	newIndex: number;
	rowAboveId: string | null;
	rowBelowId: string | null;
	movedRow: T;
	newData: T[];
}) => void;

export type RowReorderHandler<T extends TableData> = (newData: T[]) => void;

export function getTableMeta<T extends TableData>(table: TanstackTable<T>): MantineTableMeta {
	return (
		(table.options.meta as MantineTableMeta) ?? {
			selectAll: false,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			setSelectAll: () => {},
			greedySelecting: false,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			setGreedySelecting: () => {},
			selectionScope: 'Page',
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			setSelectionScope: () => {},
			enablePagination: false,
			manualPagination: false,
		}
	);
}

export function getTableFilterMeta<T extends TableData>(table: TanstackTable<T>) {
	const meta = getTableMeta(table);
	return {
		showColumnFilters: meta.showColumnFilters ?? false,
		showGlobalFilter: meta.showGlobalFilter ?? false,
	};
}
